"use client";

import { createClient } from "@supabase/supabase-js";
import { useCallback, useRef, useSyncExternalStore } from "react";

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL as string;
const SUPABASE_PUBLISHABLE_KEY = process.env
  .NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY as string;

type BroadcastHandler = (payload: never) => void;

type ChannelSubscription = {
  unsubscribe: () => void;
};

const noop = () => null;
const getSubscriptionSnapshot = () => null;

async function getRealtimeAccessToken(channelName: string): Promise<string> {
  const response = await fetch("/api/realtime/token", {
    body: JSON.stringify({ channels: [channelName] }),
    headers: { "Content-Type": "application/json" },
    method: "POST",
  });
  if (!response.ok) {
    throw new Error(`Realtime authorization failed: ${response.status}`);
  }
  const body = (await response.json()) as { token?: string };
  if (!body.token) {
    throw new Error("Realtime authorization returned no token");
  }
  return body.token;
}

function createRealtimeClient(channelName: string) {
  return createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    accessToken: () => getRealtimeAccessToken(channelName),
  });
}

function subscribeToPrivateChannel(
  channelName: string,
  handlers: Record<string, BroadcastHandler>
): () => void {
  let disposed = false;
  let removeSubscription: (() => void) | undefined;
  const client = createRealtimeClient(channelName);

  client.realtime
    .setAuth()
    .then(() => {
      if (disposed) {
        return;
      }
      let channel = client.channel(channelName, {
        config: { private: true },
      });
      for (const [event, handler] of Object.entries(handlers)) {
        channel = channel.on("broadcast", { event }, ({ payload }) => {
          handler(payload as never);
        });
      }
      channel.subscribe();
      removeSubscription = () => {
        client.removeChannel(channel);
      };
    })
    .catch((error: unknown) => {
      globalThis.reportError?.(error);
    });

  return () => {
    disposed = true;
    removeSubscription?.();
  };
}

/**
 * Client-side Supabase Realtime subscription helper.
 * Usage:
 *   import { subscribeToChannel } from "@workspace/database/realtime-client";
 *
 *   useMountEffect(() => {
 *     const sub = subscribeToChannel("chat-{id}", {
 *       "new-message": (payload) => { ... },
 *       "conversation-ended": (payload) => { ... },
 *     });
 *     return () => sub.unsubscribe();
 *   }, [conversationId]);
 */
export function subscribeToChannel(
  channelName: string,
  handlers: Record<string, BroadcastHandler>
): ChannelSubscription {
  const unsubscribe = subscribeToPrivateChannel(channelName, handlers);

  return {
    unsubscribe,
  };
}

/**
 * React hook that subscribes to a Supabase Realtime channel.
 * Automatically cleans up on unmount.
 *
 * Usage:
 *   useRealtimeChannel("chat-{id}", {
 *     "new-message": (payload) => { ... },
 *   });
 */
export function useRealtimeChannel(
  channelName: string | null,
  handlers: Record<string, BroadcastHandler>
): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const subscribe = useCallback(() => {
    if (!channelName) {
      return noop;
    }

    return subscribeToPrivateChannel(channelName, handlersRef.current);
  }, [channelName]);

  useSyncExternalStore(
    subscribe,
    getSubscriptionSnapshot,
    getSubscriptionSnapshot
  );
}
